import { useEffect, useRef } from "react";
import type { UpdateQuoteConfigType } from "../../../../../../common-deprecated/types/CommonTypes";
import {
    clearFinanceRate,
    clearInsuranceRate,
    fqsInitialized,
    setInsurancePersonalized,
    startFinanceRateFetch,
    startInsuranceRateFetch,
} from "../../../redux/actions/FinanceInsuranceActions";
import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { fetchExternalPriceFunctions } from "../../../redux/actions/ExternalPriceActions";
import { SESSION_STORAGE_FINANCE_QUOTES_KEY } from "../../../utils/constants";
import {
    getPersonalFinanceInsuranceEnabled,
    showBnbInsurance,
    showBnbMonthlyRates,
} from "../../../utils/settings/bnbFinanceSettingUtils";
import { FinanceUpdateTrigger } from "../../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";
import useFinanceCalculator from "../useFinanceCalculator";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

// This hook is used to initialize data used in steps where your car configuration is available (customize, services, summary)
const useInitFinanceInsurance = (): void => {
    const externalPriceEnabled = useBnbSelector((state) => state.externalPrice.enabled);
    const step = useBnbSelector((state) => state.navigation.step);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const car = useBnbSelector((state) => state.car);
    const financeQuotes = useBnbSelector((state) => state.finance.quotes);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    // This is used to trigger a setQuotes event after the finance/insurance utils are initialized.
    // This is required when we already have a finance quote in the initial render of the component (usually from a Toyota code component).
    const saveLoadedQuotes = useRef<boolean>(!!(financeQuotes.finance || financeQuotes.insurance));

    const { updateQuote } = useFinanceCalculator(externalPriceEnabled);

    useEffect(() => {
        // Most of it is related to finance/insurance quote fetching which doesn't happen on earlier steps.
        if ([NavStep.Summary, NavStep.Services, NavStep.Customize].includes(step)) {
            // We explicitly don't provide a modelId to showMonthlyRates as we want to initialize the finance service anyway.
            const loadFinanceRates = showBnbMonthlyRates(commonSettings, bnbSettings, "", step, car);
            const loadInsuranceRates = showBnbInsurance(commonSettings, bnbSettings, car);

            // These options have been added to disable personal finance/insurance quotes for the finance/insurance calculator.
            // Usually these default to true.
            const { personalFinanceEnabled, personalInsuranceEnabled } =
                getPersonalFinanceInsuranceEnabled(bnbSettings);

            // Usually the initial fetch of a finance/insurance rate is triggered by receiving an initial quote config during the fqs service init (see below).
            // Even if there are no quotes provided, it the quote update will still be triggered as it is part of the initialization.
            // This initial quote trigger will trigger the first finance/insurance fetch.
            //
            // However, in 2 specific use cases we need to manually trigger an initial finance rate fetch.
            // - When a pre loaded quote is already provided. Usually this is because we loaded a tyCode config containing a finance/insurance quote.
            //   To prevent these from being overwritten on the initial fqs quote update, we have to prevent the initial quote update from happening (see updateQuoteConfig function).
            //   In this case the initial finance fetch needs to be executed manually.
            // - When personal finance is disabled: in this case there is also no update quote action so we have to execute a fetch manually.
            //
            // The logic above also applies for insurance.
            if (loadFinanceRates && (!personalFinanceEnabled || saveLoadedQuotes.current)) {
                dispatch(startFinanceRateFetch(FinanceUpdateTrigger.Initial));
            }
            if (loadInsuranceRates && (!personalInsuranceEnabled || saveLoadedQuotes.current)) {
                dispatch(startInsuranceRateFetch());
            }

            if (loadFinanceRates || loadInsuranceRates) {
                // Code below is largely untested for now.
                // Load the personalized finance quotes service
                if (window && window.require) {
                    // The financeQuoteService does it's own enabled checks via the T1.settings object, but these settings are no longer supported in AEM.
                    if (!window.T1) window.T1 = { settings: {} };
                    if (!window.T1.settings) window.T1.settings = {};
                    if (!window.T1.settings.finance) {
                        window.T1.settings.finance = {
                            personalFinanceEnabled: personalFinanceEnabled.toString(),
                            personalInsuranceEnabled: personalInsuranceEnabled.toString(),
                        };
                    }

                    window.require(["fqs"], (fqs: Window["fqs"]) => {
                        // Get the financial & insurance quote ID's
                        if (fqs) {
                            // Useful for testing purposes
                            // window.fqs = fqs;
                            // Using the browsers's console you can execute the following script to simulate a quote update:
                            // window.fqs.plugins.postal.publish({channel:window.fqs.contract.events.channel, topic:window.fqs.contract.events.setQuotes, data:{finance:"FINANCE_QUOTE_ID",insurance:"INSURANCE_QUOTE_ID"}});
                            try {
                                // Configure quoteid update handlers
                                fqs.callbacks({
                                    onSetQuotes: (data: UpdateQuoteConfigType) => updateQuote(data),
                                });
                                const fqsEvent = fqs.contract.events;
                                fqs.plugins.postal.publish({
                                    channel: fqsEvent.channel,
                                    topic: fqsEvent.getQuotes,
                                    data: {
                                        done: (data: UpdateQuoteConfigType) => {
                                            // Make sure the application knows that the FQS service is initialized
                                            dispatch(fqsInitialized());
                                            // If there are loaded quotes (from within a toyota code) we need to dispatch a setQuotes event
                                            // so the quotes also get saved in session storage
                                            if (saveLoadedQuotes.current) {
                                                const payLoad: any = {};
                                                if (financeQuotes.finance) payLoad.finance = financeQuotes.finance;
                                                if (financeQuotes.insurance)
                                                    payLoad.insurance = financeQuotes.insurance;
                                                fqs.plugins.postal.publish({
                                                    channel: fqsEvent.channel,
                                                    topic: fqsEvent.setQuotes,
                                                    data: payLoad,
                                                });
                                            } else {
                                                updateQuote(data);
                                            }
                                        },
                                        fail: () => updateQuote(undefined),
                                    },
                                });

                                // Listen for personalization of quotes
                                fqs.plugins.postal.subscribe({
                                    channel: fqsEvent.channel,
                                    topic: fqsEvent.setQuotes,
                                    callback: (data: UpdateQuoteConfigType) => {
                                        if (data.insurance) dispatch(setInsurancePersonalized(true));
                                    },
                                });

                                // Check if build was loaded with personalized insurance quote
                                const quotes = JSON.parse(
                                    sessionStorage.getItem(SESSION_STORAGE_FINANCE_QUOTES_KEY) || "{}",
                                ) as UpdateQuoteConfigType;
                                if (quotes.insurance) dispatch(setInsurancePersonalized(true));
                            } catch (ex) {
                                updateQuote(undefined);
                            }
                        }
                    });
                }
            }
            if (externalPriceEnabled) {
                // Initialize WLTP by starting the functions fetch if required.
                // Mass management load is encapsulated in the external price logic, hence if wltp is enabled it will be taken care off separately
                // see ExternalPriceLogic
                dispatch(fetchExternalPriceFunctions());
            }
        }
        // The quoteId should not be filled in when on the model or engine step
        else if (
            [NavStep.Model, NavStep.Engine, NavStep.ModelEngine, NavStep.Submodels, NavStep.Grades].includes(step)
        ) {
            // These options have been added to disable personal finance/insurance quotes for the finance/insurance calculator.
            // Usually these default to true.
            const { personalFinanceEnabled, personalInsuranceEnabled } =
                getPersonalFinanceInsuranceEnabled(bnbSettings);

            // The financeQuoteService does it's own enabled checks via the T1.settings object, but these settings are no longer supported in AEM.
            if (!window.T1) window.T1 = { settings: {} };
            if (!window.T1.settings) window.T1.settings = {};
            if (!window.T1.settings.finance) {
                window.T1.settings.finance = {
                    personalFinanceEnabled: personalFinanceEnabled.toString(),
                    personalInsuranceEnabled: personalInsuranceEnabled.toString(),
                };
            }

            window.require(["fqs"], (fqs: Window["fqs"]) => {
                // Get the financial & insurance quote ID's
                if (fqs) {
                    // Check if build was loaded with personalized insurance quote or finance quote
                    dispatch(clearFinanceRate());
                    dispatch(clearInsuranceRate());
                }
            });
        }
        // We don't listen to step here because we don't allow switching from model/engine to customize+ without page reload
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useInitFinanceInsurance;
